import {
  BookedSkatingPass,
  BookedSkatingPassesPaginatedResponse,
  CustomerWaiver,
  RequestedByType,
  TimeSlot,
} from '@types';
import { Pagination, PDFViewerModal, SearchBar } from '_components/atoms';
import {
  AddTicketsToSessionDateTime,
  RemoveWaiverModal,
  SearchCustomerWaivers,
  WaiverRemovalLogModal,
} from '_components/molecules';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';
import { FaFileSignature, FaReceipt } from 'react-icons/fa';

const isMaxUsesExhausted = (pass: BookedSkatingPass): boolean => {
  const maxUses = HelperMethods.getEffectiveMaxUses(pass);
  return maxUses != null && Number(pass.redeemedCount || 0) >= maxUses;
};

const getErrorMessage = (error: any): string => {
  return (
    error?.detail ||
    error?.message ||
    error?.result?.message ||
    error?.title ||
    'An error occurred while applying the pass.'
  );
};

const BookedSkatingPasses: React.FC = () => {
  const [passes, setPasses] = useState<BookedSkatingPass[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [redeemModal, setRedeemModal] = useState<{
    isOpen: boolean;
    passId: number | null;
    selectedTimeSlot: TimeSlot | null;
  }>({
    isOpen: false,
    passId: null,
    selectedTimeSlot: null,
  });
  const [redeemingPassId, setRedeemingPassId] = useState<number | null>(null);
  const [redeemError, setRedeemError] = useState('');
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });
  const [attachWaiverModal, setAttachWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [attachedWaiver, setAttachedWaiver] = useState<CustomerWaiver | null>(
    null,
  );
  const [isAttachingWaiver, setIsAttachingWaiver] = useState(false);
  const [removeWaiverModal, setRemoveWaiverModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });
  const [isRemovingWaiver, setIsRemovingWaiver] = useState(false);
  const [waiverLogModal, setWaiverLogModal] = useState<{
    isOpen: boolean;
    recordId: number | null;
  }>({
    isOpen: false,
    recordId: null,
  });

  const fetchPasses = useCallback(async () => {
    try {
      setLoading(true);

      const responseData = (await AdminService.getBookedSkatingPasses(
        pageNumber,
        pageSize,
        searchTerm,
      )) as unknown as BookedSkatingPassesPaginatedResponse;

      if (responseData) {
        setPasses(responseData.passes);
        setTotalRecords(responseData.pagination.totalRecords);
        setTotalPages(responseData.pagination.totalPages);
        setHasNext(responseData.pagination.hasNext);
        setHasPrevious(responseData.pagination.hasPrevious);
      }
    } catch (error) {
      console.error('Error fetching passes:', error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm]);

  useEffect(() => {
    fetchPasses();
  }, [fetchPasses]);

  const handleSearch = useCallback((searchTerm: string) => {
    // Only search if there are at least 3 characters or the search term is empty (to clear results)
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      setSearchTerm(searchTerm);
      setPageNumber(1); // Reset to first page when searching
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when changing page size
  };

  const handleRedeemPass = (passId: number) => {
    setRedeemError('');
    setRedeemModal({
      isOpen: true,
      passId,
      selectedTimeSlot: null,
    });
  };

  const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
    setRedeemModal(prev => ({
      ...prev,
      selectedTimeSlot: timeSlot,
    }));
  };

  const closeRedeemModal = () => {
    setRedeemModal({
      isOpen: false,
      passId: null,
      selectedTimeSlot: null,
    });
    setRedeemError('');
  };

  const confirmRedeemPass = async () => {
    if (!redeemModal.passId || !redeemModal.selectedTimeSlot) return;

    try {
      setRedeemingPassId(redeemModal.passId);
      setRedeemError('');
      await AdminService.redeemBookedSkatingPass(
        redeemModal.passId,
        redeemModal.selectedTimeSlot.id,
      );

      closeRedeemModal();
      await fetchPasses();
    } catch (error: any) {
      console.error('Error redeeming pass:', error);
      setRedeemError(getErrorMessage(error));
    } finally {
      setRedeemingPassId(null);
    }
  };

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  const confirmAttachWaiver = async () => {
    if (
      !attachedWaiver ||
      !attachWaiverModal.recordId ||
      !attachedWaiver.pdfFilePath
    )
      return;

    try {
      setIsAttachingWaiver(true);
      await AdminService.attachCustomerWaiverToRecord(
        'skating_pass',
        attachWaiverModal.recordId,
        attachedWaiver.id,
        attachedWaiver.pdfFilePath,
      );
      await fetchPasses();
      setAttachWaiverModal({ isOpen: false, recordId: null });
      setAttachedWaiver(null);
    } catch (error) {
      console.error('Error attaching waiver:', error);
    } finally {
      setIsAttachingWaiver(false);
    }
  };

  const confirmRemoveWaiver = async (
    requestedByType: RequestedByType,
    notes: string,
  ) => {
    if (!removeWaiverModal.recordId) return;

    try {
      setIsRemovingWaiver(true);
      await AdminService.removeAttachedWaiver(
        'skating_pass',
        removeWaiverModal.recordId,
        requestedByType,
        notes || undefined,
      );
      await fetchPasses();
      setRemoveWaiverModal({ isOpen: false, recordId: null });
    } catch (error) {
      console.error('Error removing waiver:', error);
    } finally {
      setIsRemovingWaiver(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Booked Skating Passes
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage and view all booked skating passes
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Records: <span className="font-semibold">{totalRecords}</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        placeholder="Search by customer name, email, pass name, or barcode ID..."
        onSearch={handleSearch}
        debounceMs={1000}
      />

      {/* Pagination Controls */}
      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchPasses}
      />

      {/* Passes Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pass Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {passes.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No passes found
                  </td>
                </tr>
              ) : (
                passes.map(pass => (
                  <tr key={pass.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">
                        Pass #{pass.skatingPassId}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pass.passName}
                      </div>
                      <div className="text-xs text-gray-700">
                        Barcode:{' '}
                        <span className="font-semibold text-blue-600">
                          {pass.barcodeId}
                        </span>
                      </div>
                      <div className="text-xs text-gray-700">
                        Created:{' '}
                        <span className="font-semibold">
                          {HelperMethods.formatDateTimeLocal(pass.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pass.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pass.customerEmail}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pass.customerPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pass.maxUses === null
                          ? `${pass.redeemedCount || 0} / Unlimited`
                          : `${pass.redeemedCount || 0} / ${pass.maxUses}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pass.maxUses === null
                          ? 'unlimited passes'
                          : '# passes used'}
                      </div>
                      {pass.maxUses !== null && (
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${(Number(pass.redeemedCount || 0) / Number(pass.maxUses)) * 100}%`,
                            }}
                          ></div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-between">
                        {pass.waiverType === 'Paper' && !pass.waiverUrl ? (
                          <div className="flex items-center space-x-2">
                            <div className="text-xs text-green-700">
                              <div className="font-semibold">Paper waiver</div>
                              {pass.paperWaiverCompletedAt && (
                                <div className="text-gray-500">
                                  {HelperMethods.formatDateTimeLocal(
                                    pass.paperWaiverCompletedAt,
                                  )}
                                </div>
                              )}
                              {pass.paperWaiverRecordedByName && (
                                <div className="text-gray-500">
                                  by {pass.paperWaiverRecordedByName}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() =>
                                setRemoveWaiverModal({
                                  isOpen: true,
                                  recordId: pass.id,
                                })
                              }
                              className="px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Remove Waiver
                            </button>
                          </div>
                        ) : pass.waiverUrl || pass.waiverId > 0 ? (
                          <div className="flex items-center space-x-2">
                            {pass.waiverUrl && (
                              <button
                                onClick={() =>
                                  openPdfViewer(pass.waiverUrl!, 'View Waiver')
                                }
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <FaFileSignature className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              onClick={() =>
                                setRemoveWaiverModal({
                                  isOpen: true,
                                  recordId: pass.id,
                                })
                              }
                              className="px-3 py-1 text-xs rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Remove Waiver
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setAttachedWaiver(null);
                              setAttachWaiverModal({
                                isOpen: true,
                                recordId: pass.id,
                              });
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Attach Waiver
                          </button>
                        )}
                        <button
                          onClick={() =>
                            setWaiverLogModal({
                              isOpen: true,
                              recordId: pass.id,
                            })
                          }
                          className="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Waiver Log
                        </button>
                        {pass.receiptUrl && (
                          <button
                            onClick={() =>
                              openPdfViewer(pass.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaReceipt className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRedeemPass(pass.id)}
                          disabled={
                            redeemingPassId === pass.id ||
                            isMaxUsesExhausted(pass)
                          }
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            redeemingPassId === pass.id ||
                            isMaxUsesExhausted(pass)
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                          }`}
                        >
                          {redeemingPassId === pass.id
                            ? 'Applying...'
                            : 'Apply One Pass'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Passes Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {passes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No passes found
          </div>
        ) : (
          passes.map(pass => (
            <div
              key={pass.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Pass Info */}
              <div className="mb-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pass #{pass.skatingPassId}
                  </h3>
                </div>
                <div className="text-sm text-gray-600">{pass.passName}</div>
                <div className="text-xs text-gray-700 mt-1">
                  Barcode:{' '}
                  <span className="font-semibold text-blue-600">
                    {pass.barcodeId}
                  </span>
                </div>
                <div className="text-xs text-gray-700 mt-1">
                  Created:{' '}
                  <span className="font-semibold">
                    {HelperMethods.formatDateTimeLocal(pass.createdAt)}
                  </span>
                </div>
              </div>

              {/* Usage Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Usage
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">
                    {pass.maxUses === null
                      ? `${pass.redeemedCount || 0} / Unlimited uses`
                      : `${pass.redeemedCount || 0} / ${pass.maxUses} uses`}
                  </div>
                  {pass.maxUses !== null && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(Number(pass.redeemedCount || 0) / Number(pass.maxUses)) * 100}%`,
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{pass.customerName}</div>
                  <div>{pass.customerEmail}</div>
                  <div>{pass.customerPhone}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {pass.waiverType === 'Paper' && !pass.waiverUrl ? (
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-green-700">
                      <div className="font-semibold">Paper waiver</div>
                      {pass.paperWaiverCompletedAt && (
                        <div className="text-gray-500">
                          {HelperMethods.formatDateTimeLocal(
                            pass.paperWaiverCompletedAt,
                          )}
                        </div>
                      )}
                      {pass.paperWaiverRecordedByName && (
                        <div className="text-gray-500">
                          by {pass.paperWaiverRecordedByName}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        setRemoveWaiverModal({
                          isOpen: true,
                          recordId: pass.id,
                        })
                      }
                      className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                    >
                      Remove Waiver
                    </button>
                  </div>
                ) : pass.waiverUrl || pass.waiverId > 0 ? (
                  <div className="flex items-center gap-2">
                    {pass.waiverUrl && (
                      <button
                        onClick={() =>
                          openPdfViewer(pass.waiverUrl!, 'View Waiver')
                        }
                        className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                      >
                        <FaFileSignature className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() =>
                        setRemoveWaiverModal({
                          isOpen: true,
                          recordId: pass.id,
                        })
                      }
                      className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                    >
                      Remove Waiver
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setAttachedWaiver(null);
                      setAttachWaiverModal({
                        isOpen: true,
                        recordId: pass.id,
                      });
                    }}
                    className="inline-flex items-center px-3 py-2 bg-green-100 text-green-700 text-sm rounded-md hover:bg-green-200 transition-colors"
                  >
                    Attach Waiver
                  </button>
                )}
                <button
                  onClick={() =>
                    setWaiverLogModal({ isOpen: true, recordId: pass.id })
                  }
                  className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors"
                >
                  Waiver Log
                </button>
                {pass.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(pass.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    <FaReceipt className="w-5 h-5" />
                  </button>
                )}
                {pass.isActive && !isMaxUsesExhausted(pass) && (
                  <button
                    onClick={() => handleRedeemPass(pass.id)}
                    disabled={redeemingPassId === pass.id}
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      redeemingPassId === pass.id
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                    }`}
                  >
                    {redeemingPassId === pass.id
                      ? 'Applying...'
                      : 'Apply One Pass'}
                  </button>
                )}
              </div>

              {/* Last Used Info */}
              {pass.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Last used by: {pass.redeemedBy}
                </div>
              )}
              {pass.redeemedAt && (
                <div className="mt-1 text-xs text-gray-500">
                  Last used:{' '}
                  {HelperMethods.formatDateTimeLocal(pass.redeemedAt)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Redeem Pass Modal */}
      {redeemModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Apply One Pass - Select Session
              </h2>
              <button
                onClick={closeRedeemModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {redeemError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {redeemError}
                </div>
              )}

              <AddTicketsToSessionDateTime
                onSelectTimeSlot={handleTimeSlotSelect}
                selectedTimeSlot={redeemModal.selectedTimeSlot || undefined}
              />
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeRedeemModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRedeemPass}
                disabled={
                  !redeemModal.selectedTimeSlot || redeemingPassId !== null
                }
                className={`px-4 py-2 rounded-lg transition-colors ${
                  redeemModal.selectedTimeSlot && redeemingPassId === null
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {redeemingPassId !== null ? 'Applying...' : 'Apply One Pass'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />

      {attachWaiverModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Attach Waiver
              </h2>
              <button
                onClick={() => {
                  setAttachWaiverModal({ isOpen: false, recordId: null });
                  setAttachedWaiver(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <SearchCustomerWaivers
                onAttach={waiver => setAttachedWaiver(waiver)}
                onUnattach={() => setAttachedWaiver(null)}
                attachedWaiverId={`${attachedWaiver?.pdfFilePath}_${attachedWaiver?.barcodeId}`}
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setAttachWaiverModal({ isOpen: false, recordId: null });
                  setAttachedWaiver(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={confirmAttachWaiver}
                disabled={!attachedWaiver?.pdfFilePath || isAttachingWaiver}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  attachedWaiver?.pdfFilePath && !isAttachingWaiver
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                type="button"
              >
                {isAttachingWaiver ? 'Attaching...' : 'Confirm Attach'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RemoveWaiverModal
        isOpen={removeWaiverModal.isOpen}
        isRemoving={isRemovingWaiver}
        onClose={() => setRemoveWaiverModal({ isOpen: false, recordId: null })}
        onConfirm={confirmRemoveWaiver}
      />
      <WaiverRemovalLogModal
        isOpen={waiverLogModal.isOpen}
        bookingType="skating_pass"
        recordId={waiverLogModal.recordId}
        onClose={() => setWaiverLogModal({ isOpen: false, recordId: null })}
        onViewWaiver={url => openPdfViewer(url, 'Removed Waiver')}
      />
    </div>
  );
};

export default BookedSkatingPasses;
